// Goods in: what the automatic CofA intake job could not finish (rule 25).
//
// Recording is automatic; deciding never is. The job records every certificate it can match in
// QA@; this lists the ones it could not — no PO line found, a scan a person has to read, goods not
// received yet, a failure — each with the reason. It says when the job is off or has not run, so
// an empty list is never read as "nothing arrived" (rule 15).
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { officeFocus, useResource } from '../../shells/OfficeShell';
import { withoutCodes } from '../../lib/vocabulary';
import { RecordByHand } from './RecordByHand';
import { INTAKE_LABEL, INTAKE_TONE, TONE_CLASS, chipClass, intakeStanding, stamp, type IntakePayload, type IntakeRow } from './goodsInModel';

const col = createColumnHelper<IntakeRow>();

/** (UI/UX audit, 31 Oct 2026, UX-03) The server returns at most `limit` rows and, since then,
 *  `total`: how many match. Optional here so an older API answer still renders (no "of N"). */
type IntakeRead = IntakePayload & { total?: number; limit?: number; truncated?: boolean };

/** The Intake table's columns. `onRecorded` re-reads the panel: a recorded attachment leaves the list. */
const columnsFor = (onRecorded: () => void) => [
  // The sender rides in the accessor so the search finds it (UX-03); the ISO date leads, so the
  // column still sorts by when the email arrived.
  col.accessor((r) => `${r.emailReceivedAt ?? ''} ${r.senderAddress ?? ''}`, {
    id: 'received',
    header: 'Email',
    cell: (c) => {
      const r = c.row.original;
      return (
        <>
          <span className="block whitespace-nowrap">{stamp(r.emailReceivedAt)}</span>
          <span className="block text-(length:--fs-xs) text-(--muted)">{r.senderAddress ?? '—'}</span>
        </>
      );
    },
    meta: { width: '11rem' },
  }),
  col.accessor((r) => `${r.subject ?? ''} ${r.attachmentName}`, {
    id: 'attachment',
    header: 'Attachment',
    cell: (c) => {
      const r = c.row.original;
      return (
        <>
          <span className="line-clamp-2" title={r.attachmentName}>
            {r.attachmentName}
          </span>
          <span className="block text-(length:--fs-xs) text-(--muted) line-clamp-1" title={r.subject ?? ''}>
            {r.subject ?? '(no subject)'}
          </span>
        </>
      );
    },
    meta: { rowHeader: true, width: '16rem' },
  }),
  col.accessor((r) => INTAKE_LABEL[r.outcome], {
    id: 'outcome',
    header: 'Outcome',
    cell: (c) => {
      const r = c.row.original;
      return (
        <span className={`${chipClass} ${TONE_CLASS[INTAKE_TONE[r.outcome]]}`}>
          {INTAKE_LABEL[r.outcome]}
          {r.dryRun && r.outcome !== 'DRYRUN' ? ' (dry run)' : ''}
        </span>
      );
    },
    meta: { nowrap: true, width: '11rem' },
  }),
  col.accessor((r) => r.reason ?? '', {
    id: 'reason',
    header: 'Why',
    // Bracketed codes ("(attachment_ambiguous)", "(UNMATCHED: …)") taken out for display; the stored
    // reason, codes and all, stays in the tooltip (UI/UX audit, 31 Oct 2026, UX-18).
    cell: (c) => <span className="line-clamp-3" title={c.getValue()}>{withoutCodes(c.getValue()) || '—'}</span>,
  }),
  col.accessor((r) => (r.po ? `${r.po}${r.line && r.line !== 1 ? ` / ${r.line}` : ''}` : ''), {
    id: 'po',
    header: 'PO',
    cell: (c) =>
      c.row.original.receiptId ? (
        <Link
          to={`/office/goods-in/${c.row.original.receiptId}`}
          className={`underline decoration-(--border-strong) underline-offset-[3px] hover:decoration-current ${officeFocus}`}
        >
          {c.getValue() || `#${c.row.original.receiptId}`}
        </Link>
      ) : (
        '—'
      ),
    meta: { mono: true, nowrap: true, width: '8.5rem' },
  }),
  // Record by hand (#152 B1): what the job could not record, a person reads and types. Never on a dry-run row.
  col.display({
    id: 'record',
    header: '',
    cell: (c) => (c.row.original.dryRun ? null : <RecordByHand row={c.row.original} onRecorded={onRecorded} />),
    meta: { nowrap: true, width: '9rem' },
  }),
];

const MODE_TEXT: Record<IntakePayload['mode'], string> = {
  off: 'Automatic recording is off: certificates are recorded through Claude by a person.',
  dryrun: 'Automatic recording is in a dry run: it reads QA@ and says what it would record, and records nothing.',
  on: 'Certificates in QA@ are recorded automatically. A person still makes every decision.',
};

/** Tells the tab its count: a number once read, null when it could not be read (rule 15: never 0 for unknown). */
export function GoodsInIntake({ onCount }: { onCount?: (n: number | null) => void } = {}) {
  const intake = useResource<IntakeRead>('/cofa-intake');
  const data = intake.data;
  // Review 8: once the job is on, its dry-run rows are history, not work.
  const rows = (data?.rows ?? []).filter((r) => data?.mode !== 'on' || !r.dryRun);
  // The tab's count: what needs a person here (the audit's last open item). (UX-03) The server's
  // uncapped total, never the rows it sent: at the 200 cap the tab read "200" as if it were all.
  // This counts ATTACHMENTS; the Goods in nav badge counts the register's DECISIONS. Two different
  // things, never added together - the tab's accessible name and the sentence below say which.
  const total = data ? (data.total ?? rows.length) : null;
  const truncated = data ? total !== null && total > rows.length : false;
  useEffect(() => onCount?.(intake.error ? null : total), [intake.error, total, onCount]);
  const standing = data ? intakeStanding(data, new Date()) : null;
  const reload = intake.reload;
  const columns = useMemo(() => columnsFor(reload), [reload]);
  return (
    <section aria-labelledby="intake-heading" className="flex flex-col gap-(--s3)">
      <div>
        <h2 id="intake-heading" className="m-0 font-display text-[22px] font-semibold leading-tight">
          Intake
        </h2>
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)" aria-live="polite">
          {data ? MODE_TEXT[data.mode] : ''}
        </p>
        {standing ? (
          <p
            role={standing.known ? undefined : 'status'}
            className={
              standing.known
                ? 'm-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)'
                : `m-0 mt-(--s2) rounded-(--radius) border p-(--s2) text-(length:--fs-sm) font-semibold ${TONE_CLASS.warn}`
            }
          >
            {standing.text}
          </p>
        ) : null}
        {/* (UX-03) Say what the tab's number counts, and when the list is not all of it. */}
        {data && !intake.error && total ? (
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
            {truncated
              ? `Showing the newest ${rows.length} of ${total} QA@ attachments that need a person. Deal with these and the rest come up.`
              : `${total} QA@ attachment${total === 1 ? '' : 's'} need${total === 1 ? 's' : ''} a person.`}{' '}
            These are emails, not the register's decisions the Goods in badge counts.
          </p>
        ) : null}
      </div>
      {intake.error ? (
        <p role="alert" className={`m-0 flex flex-wrap items-center gap-(--s3) rounded-(--radius) border p-(--s3) text-(length:--fs-sm) font-semibold ${TONE_CLASS.warn}`}>
          Could not read what the intake job did: {intake.error}
          <Button variant="outline" type="button" onClick={() => intake.reload()}>
            Try again
          </Button>
        </p>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          caption={`QA@ attachments the intake job could not record, newest email first${truncated ? `: the newest ${rows.length} of ${total}` : ''}`}
          search="Search sender, attachment, subject or PO"
          getRowId={(r) => String(r.id)}
          loading={intake.loading}
          empty={
            data && standing
              ? standing.known
                ? 'Nothing from QA@ is waiting on a person.'
                : 'Whether anything from QA@ is waiting on a person is not known (above).'
              : null
          }
        />
      )}
    </section>
  );
}

export default GoodsInIntake;
